import { HTMLAttributes, MouseEventHandler, ReactNode, useId } from "react";
import { VisuallyHidden } from "radix-ui";
import classNames from "classnames/bind";
import { ButtonLink } from "app/components/ui/ButtonLink/ButtonLink";
import {
	Card,
	CardBody,
	CardContent,
	CardDescription,
	CardLinkOverlay,
	CardMedia,
	CardTitle,
	CardTitleProps,
} from "app/components/ui/Card/Card";
import { Image } from "app/components/ui/Image/Image";
import { LinkProps } from "app/components/ui/Link/Link";
import { ArrowRightIcon } from "app/icons/navigation";
import styles from "./CompactHotelCard.module.scss";

const cx = classNames.bind(styles);

export type CompactHotelCardPhoto = { url: string; thumbnail?: string };

export type CompactHotelCardProps = {
	/** Offer the card opens: the title is a link stretched over the whole card. */
	to: LinkProps["to"];
	target?: LinkProps["target"];
	/** History state passed to the offer page. */
	state?: LinkProps["state"];
	/** Fired by a click on the card, before the navigation. */
	onClick?: MouseEventHandler<HTMLAnchorElement>;
	/** Heading of the card, also its accessible name. Cut to one line with a subtitle, two without. */
	title: ReactNode;
	/** Line under the title, cut to one line. */
	subtitle?: ReactNode;
	/** Level of the heading that holds the title. Defaults to 3. */
	headingLevel?: CardTitleProps["headingLevel"];
	photo?: CompactHotelCardPhoto;
	/** Badges laid over the top of the photo. */
	badges?: ReactNode;
	/** Rating block. It must not be interactive: the whole card is clickable. */
	rating?: ReactNode;
	/** Price block, at the bottom of the card. */
	price?: ReactNode;
	/** Line under the price, e.g. "7 nights, flight included". */
	description?: ReactNode;
	/** Shows the call-to-action button at the bottom end: a mouse-only copy of the card link, hidden from keyboard and assistive technologies. */
	showCta?: boolean;
	/** Outlines the card as the hotel the user searched for, and adds this label to its accessible name. */
	spotlightLabel?: ReactNode;
} & Omit<HTMLAttributes<HTMLElement>, "children" | "title" | "onClick">;

/** Small horizontal hotel card: photo on the side, the whole surface opens the offer. */
export const CompactHotelCard = ({
	title,
	subtitle,
	headingLevel,
	photo,
	badges,
	rating,
	price,
	description,
	showCta = false,
	spotlightLabel,
	to,
	target,
	state,
	onClick,
	className,
	...rest
}: CompactHotelCardProps) => {
	const titleId = useId();
	const hasSubtitle = Boolean(subtitle);
	const isSpotlighted = Boolean(spotlightLabel);

	return (
		<Card asChild variant="elevated" orientation="horizontal" padding="sm">
			<article
				aria-labelledby={titleId}
				data-has-subtitle={hasSubtitle}
				data-spotlight={isSpotlighted}
				data-testid="compact-hotel-card"
				className={cx("compact-hotel-card", className)}
				{...rest}
			>
				<CardMedia className={cx("compact-hotel-card__media")}>
					{photo && (
						<Image
							fill
							src={photo.url}
							thumbnail={photo.thumbnail}
							alt=""
							width={104}
							height={104}
							sizes="104px"
						/>
					)}

					{badges && <div className={cx("compact-hotel-card__badges")}>{badges}</div>}
				</CardMedia>

				<CardBody>
					<CardContent className={cx("compact-hotel-card__content")}>
						<hgroup>
							<CardTitle
								headingLevel={headingLevel}
								variant="large"
								className={cx("compact-hotel-card__title")}
							>
								<CardLinkOverlay
									to={to}
									target={target}
									state={state}
									id={titleId}
									onClick={onClick}
								>
									{title}

									{isSpotlighted && (
										<>
											{" "}
											<VisuallyHidden.Root>
												{spotlightLabel}
											</VisuallyHidden.Root>
										</>
									)}
								</CardLinkOverlay>
							</CardTitle>

							{hasSubtitle && (
								<CardDescription
									variant="small"
									isBold
									className={cx("compact-hotel-card__subtitle")}
								>
									{subtitle}
								</CardDescription>
							)}
						</hgroup>

						{rating}

						<div className={cx("compact-hotel-card__footer")}>
							<div className={cx("compact-hotel-card__price")}>
								{price}

								{description && (
									<CardDescription
										data-testid="compact-hotel-card-description"
										variant="small"
									>
										{description}
									</CardDescription>
								)}
							</div>

							{showCta && (
								<ButtonLink
									to={to}
									target={target}
									state={state}
									onClick={onClick}
									tabIndex={-1}
									aria-hidden="true"
									variant="primary"
									size="icon-sm"
									startIcon={<ArrowRightIcon />}
								/>
							)}
						</div>
					</CardContent>
				</CardBody>
			</article>
		</Card>
	);
};
